<template>
  <v-dialog :value="value" max-width="680" @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        Dela {{ dashboard.name }}
        <v-spacer></v-spacer>
        <v-btn icon aria-label="Stäng" @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-skeleton-loader v-if="isLoading" type="paragraph, actions"></v-skeleton-loader>
        <template v-else-if="sharing.active">
          <v-alert text type="success">
            Dashboarden kan visas av alla som har länken. Ändringar av kort och layout syns automatiskt på den delade dashboarden.
          </v-alert>
          <v-text-field
            :value="sharedUrl"
            label="Delningslänk"
            readonly
            outlined
            hide-details
            append-icon="mdi-content-copy"
            @click:append="copyLink"
          ></v-text-field>
          <div class="d-flex flex-wrap mt-4" style="gap: 8px;">
            <v-btn color="primary" depressed @click="copyLink">
              <v-icon left>mdi-content-copy</v-icon>
              Kopiera länk
            </v-btn>
            <v-btn text :href="sharedUrl" target="_blank" rel="noopener noreferrer">
              <v-icon left>mdi-open-in-new</v-icon>
              Öppna
            </v-btn>
            <v-spacer></v-spacer>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn text :disabled="isSaving" v-bind="attrs" v-on="on">
                  Hantera länk
                  <v-icon right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="confirmRegenerate">
                  <v-list-item-icon>
                    <v-icon>mdi-refresh</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Skapa om länken</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item @click="confirmRevoke">
                  <v-list-item-icon>
                    <v-icon color="error">mdi-link-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text">Återkalla länken</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
        <template v-else>
          <div class="d-flex flex-column">
            <v-alert class="mt-4 mb-8" text type="warning">
              Alla som får länken kan se samtliga kort och all information på dashboarden utan att logga in.
            </v-alert>
            <v-btn
              class="ml-auto" color="success" depressed :loading="isSaving"
              @click="activate"
            >
              <v-icon left>mdi-share-variant</v-icon>
              Skapa delningslänk
            </v-btn>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import DashboardService from '@/services/DashboardService.js'

export default {
  name: 'DashboardShareDialog',
  props: {
    value: Boolean,
    dashboard: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    isLoading: false,
    isSaving: false,
    sharing: {
      active: false,
      namespace: '',
      public_link_token: null
    }
  }),
  computed: {
    sharedUrl: function () {
      if (!this.sharing.active || !this.sharing.namespace || !this.sharing.public_link_token) {
        return ''
      }
      const origin = process.env.IS_ELECTRON ? 'https://app.campingonline.se' : window.location.origin
      return `${origin}/${encodeURIComponent(this.sharing.namespace)}/shared/dashboard/${encodeURIComponent(this.sharing.public_link_token)}`
    }
  },
  methods: {
    fetchSharing: function () {
      this.isLoading = true
      DashboardService.getSharedLink(this.dashboard.id)
        .then(({ data }) => {
          this.sharing = data.data
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    activate: function () {
      this.isSaving = true
      DashboardService.activateSharedLink(this.dashboard.id)
        .then(({ data }) => this.applySharing(data.data))
        .finally(() => {
          this.isSaving = false
        })
    },
    confirmRegenerate: function () {
      this.$store.commit('SET_OPEN_CONFIRMATION_DIALOG', {
        header: 'Skapa om delningslänken',
        body: 'Den nuvarande länken slutar fungera direkt. Vill du skapa en ny delningslänk?',
        confirmationFunction: this.regenerate
      })
    },
    regenerate: function () {
      this.isSaving = true
      DashboardService.regenerateSharedLink(this.dashboard.id)
        .then(({ data }) => this.applySharing(data.data))
        .finally(() => {
          this.isSaving = false
        })
    },
    confirmRevoke: function () {
      this.$store.commit('SET_OPEN_CONFIRMATION_DIALOG', {
        header: 'Återkalla delningslänken',
        body: 'Den delade dashboarden slutar vara tillgänglig direkt. Vill du återkalla länken?',
        confirmationFunction: this.revoke
      })
    },
    revoke: function () {
      this.isSaving = true
      DashboardService.revokeSharedLink(this.dashboard.id)
        .then(({ data }) => this.applySharing(data.data))
        .finally(() => {
          this.isSaving = false
        })
    },
    applySharing: function (sharing) {
      this.sharing = sharing
      this.$emit('sharing-changed', { active: sharing.active })
    },
    copyLink: async function () {
      if (!this.sharedUrl) {
        return
      }
      try {
        await navigator.clipboard.writeText(this.sharedUrl)
        this.$store.dispatch('snackbars/createSnackbar', {
          color: 'success',
          text: 'Delningslänken har kopierats'
        })
      } catch {
        this.$store.dispatch('snackbars/createSnackbar', {
          color: 'error',
          text: 'Delningslänken kunde inte kopieras'
        })
      }
    }
  },
  watch: {
    value: function (isOpen) {
      if (isOpen) {
        this.fetchSharing()
      }
    },
    'dashboard.id': function () {
      if (this.value) {
        this.fetchSharing()
      }
    }
  }
}
</script>
